import { useState } from 'react'
import { Link as RouterLink, Outlet, useParams } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import MenuIcon from '@mui/icons-material/Menu'
import { useGetManifestQuery } from '../api/authoring'
import { AuthoringScopeProvider } from '../editors/scope'
import { CommandPalette, useCommandPalette } from '../ui/CommandPalette'
import { SideNav, SIDEBAR_WIDTH } from './SideNav'

/**
 * The frame: a column of navigation, and the work beside it.
 *
 * **The shell is two regions and nothing else.** Everything that says
 * *where you are* — the wordmark, the tenant switcher, the mode switch,
 * the libraries, the sites — is in `SideNav`; everything that says *what
 * you are doing* is the screen. The app bar this replaced had to carry
 * both, which is why it wore three navigation idioms in about 500px and
 * still had no room to name a site.
 *
 * **The screen gets the whole content region.** There is no container
 * cap here and no per-screen width: a screen is drawn in the width it is
 * given, and the sidebar is the only thing taking any of it. That matters
 * most to the one layout whose second column is a rendering of a real
 * page — a preview frame is only truthful at a width a browser would
 * actually use, and a 1200px cap was rendering a customer's site at its
 * own phone breakpoints and calling it the page.
 *
 * Below `md` the column is a drawer over the work rather than beside it,
 * because at that size the two regions do not both fit and the work is
 * the one that came first. It closes on navigation: a nav drawer left
 * standing over the screen the reader just asked for is a modal they have
 * to dismiss to see what they chose.
 */
/**
 * The tenant and manifest every generated form is drawn inside.
 *
 * Mounted once here rather than threaded, because a control that offers
 * the tenant's components needs a fact the schema cannot carry — see
 * `editors/scope`. The manifest query is the one every screen already
 * makes, so this shares its cache rather than adding a read.
 *
 * `useParams` resolves the whole matched route tree, not just this
 * component's own route, which is the same thing SideNav relies on to
 * know which tenant it is drawing. Outside a tenant's routes it is
 * undefined, the query is skipped, and a form drawn there offers nothing
 * to pick — the degradation the scope's default is written for.
 */
function Scope({ children }: { children: React.ReactNode }) {
  const { tenant } = useParams()
  const manifest = useGetManifestQuery({ tenant: tenant ?? '' }, { skip: !tenant })
  return (
    <AuthoringScopeProvider value={{ tenant: tenant ?? '', manifest: manifest.data }}>
      {children}
    </AuthoringScopeProvider>
  )
}

export function AppShell() {
  const theme = useTheme()
  const beside = useMediaQuery(theme.breakpoints.up('md'))
  const [open, setOpen] = useState(false)
  // **The palette is a third region, and it is mounted once here.** It
  // is opened from a keystroke that has to work on every screen, and it
  // closes itself on navigation, so the only place it can live is the
  // frame both of those are true of. The state is the shell's because
  // the sidebar's search button opens the same thing the shortcut does —
  // two ways in, one palette.
  const palette = useCommandPalette()

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={beside ? 'permanent' : 'temporary'}
          open={open}
          onClose={() => setOpen(false)}
          // The drawer's contents are the navigation of the whole app, so
          // they stay mounted: remounting them on every open would refetch
          // nothing (the queries are cached) but would throw away the
          // scroll position in a list that can run to thirty entries.
          ModalProps={{ keepMounted: true }}
          // The landmark is the drawer's own paper rather than a box
          // around the drawer: below `md` the drawer is portalled to the
          // end of the body, and a `<nav>` wrapped round it was left
          // empty while the entries opened in no landmark at all.
          PaperProps={{ component: 'nav' }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
          }}
        >
          <SideNav onNavigate={() => setOpen(false)} onSearch={palette.show} />
        </Drawer>
      </Box>

      <CommandPalette open={palette.open} onClose={palette.hide} />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!beside && (
          <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Toolbar variant="dense" sx={{ gap: 1 }}>
              <IconButton edge="start" onClick={() => setOpen(true)} aria-label="Open the navigation">
                <MenuIcon />
              </IconButton>
              <Typography
                component={RouterLink}
                to="/"
                variant="h3"
                sx={{ textDecoration: 'none', color: 'text.primary' }}
              >
                Bluu CMS
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3, xl: 4 } }}>
          <Scope>
            <Outlet />
          </Scope>
        </Box>
      </Box>
    </Box>
  )
}
